import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect, useHistory } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardCheck,
  FaClipboardList,
  FaChevronRight,
  FaGift,
  FaHome,
  FaSearch,
  FaSun,
  FaSyncAlt,
  FaTimesCircle,
  FaUserClock,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Created By Cursor
function getCurrentYearMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

// Created By Cursor
function parseLooseDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  let m = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

// Created By Cursor
function buildSessionMonths(startRaw, endRaw) {
  const session = SessionManager.getCurrentSession()?.[0];
  const start =
    parseLooseDate(startRaw) || parseLooseDate(session?.start_date);
  const end = parseLooseDate(endRaw) || parseLooseDate(session?.end_date);
  if (!start || !end) return [getCurrentYearMonth()];
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  if (cursor > last) return [getCurrentYearMonth()];
  while (cursor <= last) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, "0");
    months.push(`${year}-${month}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months.length ? months : [getCurrentYearMonth()];
}

// Created By Cursor
function pickDefaultMonth(months) {
  const current = getCurrentYearMonth();
  if (!months?.length) return current;
  if (months.includes(current)) return current;
  if (current < months[0]) return months[0];
  return months[months.length - 1];
}

// Created By Cursor
function formatMonthLabel(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return ym || "—";
  const date = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

// Created By Cursor
function formatMonthShort(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return ym || "—";
  const date = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return date.toLocaleDateString("en-GB", { month: "short" });
}

// Created By Cursor
function monthYear(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  return m ? m[1] : "";
}

// Created By Cursor
function splitYearMonth(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) {
    const current = getCurrentYearMonth().split("-");
    return { year: current[0], month: current[1] };
  }
  return { year: m[1], month: m[2] };
}

// Created By Cursor
function staffInitials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] || "")
    .join("")
    .toUpperCase();
}

function detailPath(staff, month) {
  const path = `/AdminMain/StaffAttendanceDetail/${encodeURIComponent(
    staff.staff_id || ""
  )}/${encodeURIComponent(staff.staff_name || "Staff")}`;
  return month ? `${path}?month=${encodeURIComponent(month)}` : path;
}

function normalizeMark(status) {
  const value = String(status || "").trim();
  if (!value || value === "Not Taken") return "";
  return value;
}

function KpiChip({ tone, icon: Icon, label, value }) {
  return (
    <div className={`sm-kpi-chip sm-kpi-chip--${tone}`}>
      <span className="sm-kpi-chip__icon" aria-hidden>
        {Icon ? <Icon /> : null}
      </span>
      <div className="sm-kpi-chip__text">
        <span className="sm-kpi-chip__label">{label}</span>
        <span className="sm-kpi-chip__value">{value}</span>
      </div>
    </div>
  );
}

function aggregateStaffMonth(days) {
  const map = {};
  let weekends = 0;
  let holidays = 0;
  let schoolDays = 0;
  const monthDays = Array.isArray(days) ? days.length : 0;

  (days || []).forEach((day) => {
    const status = String(day?.status || "").trim();
    if (/^sunday$/i.test(status)) weekends += 1;
    else if (/^holiday$/i.test(status)) holidays += 1;
    else schoolDays += 1;

    (day?.staff || []).forEach((s) => {
      if (!s?.staff_id) return;
      if (!map[s.staff_id]) {
        map[s.staff_id] = {
          ...s,
          P: 0,
          A: 0,
          L: 0,
          HD: 0,
          LA: 0,
          taken_days: 0,
        };
      }
      const mark = normalizeMark(s.attendance_status);
      if (!mark) return;
      map[s.staff_id].taken_days += 1;
      if (mark === "P" || mark === "Present") map[s.staff_id].P += 1;
      else if (mark === "A" || mark === "Absent") map[s.staff_id].A += 1;
      else if (mark === "L" || mark === "Leave") map[s.staff_id].L += 1;
      else if (mark === "HD" || mark === "Half Day") map[s.staff_id].HD += 1;
      else if (mark === "LA" || mark === "Late Attendance") map[s.staff_id].LA += 1;
    });
  });

  return {
    staff: Object.values(map),
    monthStats: {
      month_days: monthDays,
      weekends,
      holidays,
      school_days: schoolDays,
    },
  };
}

// Created By Cursor
export default function StaffAttendance() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const session = SessionManager.getCurrentSession()?.[0] || {};
  const monthOptions = useMemo(
    () =>
      buildSessionMonths(
        session.START_DATE || session.start_date,
        session.END_DATE || session.end_date
      ),
    [session.START_DATE, session.start_date, session.END_DATE, session.end_date]
  );

  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() =>
    pickDefaultMonth(monthOptions)
  );
  const [staffRows, setStaffRows] = useState([]);
  const [search, setSearch] = useState("");
  const [monthStats, setMonthStats] = useState({
    month_days: 0,
    weekends: 0,
    holidays: 0,
    school_days: 0,
  });

  const currentYearMonth = useMemo(() => getCurrentYearMonth(), []);
  const selectedMonthParts = useMemo(
    () => splitYearMonth(selectedMonth),
    [selectedMonth]
  );
  const selectedMonthLabel = useMemo(
    () => formatMonthLabel(selectedMonth),
    [selectedMonth]
  );

  const totals = useMemo(() => {
    const present = staffRows.reduce((n, s) => n + Number(s.P || 0), 0);
    const absent = staffRows.reduce((n, s) => n + Number(s.A || 0), 0);
    const leave = staffRows.reduce((n, s) => n + Number(s.L || 0), 0);
    const halfDay = staffRows.reduce((n, s) => n + Number(s.HD || 0), 0);
    const late = staffRows.reduce((n, s) => n + Number(s.LA || 0), 0);
    const taken = staffRows.reduce((n, s) => n + Number(s.taken_days || 0), 0);
    const avgPct = taken === 0 ? 0 : Math.floor((present * 100) / taken);
    return {
      present,
      absent,
      leave,
      halfDay,
      late,
      count: staffRows.length,
      avgPct,
    };
  }, [staffRows]);

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staffRows;
    return staffRows.filter((s) => {
      const name = String(s.staff_name || "").toLowerCase();
      const id = String(s.staff_id || "").toLowerCase();
      const role = String(s.staff_role || "").toLowerCase();
      return name.includes(q) || id.includes(q) || role.includes(q);
    });
  }, [staffRows, search]);

  const loadStaff = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setStaffRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = {
        school_code: schoolCode,
        month: selectedMonthParts.month,
        year: selectedMonthParts.year,
      };
      const url = `${API_BASE()}getStaffAttendanceOfMonthYear.php`;
      const response = await axios.get(url, { params }).then((res) => res.data);
      if (response?.api_status === "OK") {
        const aggregated = aggregateStaffMonth(
          Array.isArray(response.data) ? response.data : []
        );
        setStaffRows(aggregated.staff);
        setMonthStats(aggregated.monthStats);
      } else {
        setStaffRows([]);
        setMonthStats({
          month_days: 0,
          weekends: 0,
          holidays: 0,
          school_days: 0,
        });
        myToast.notify_warning(
          response?.api_remark || "Could not load staff attendance."
        );
      }
    } catch (error) {
      setStaffRows([]);
      myToast.notify_warning("Failed to load staff attendance.");
    } finally {
      setLoading(false);
    }
  }, [selectedMonthParts.month, selectedMonthParts.year]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!monthOptions.length) return;
    setSelectedMonth((prev) =>
      monthOptions.includes(prev) ? prev : pickDefaultMonth(monthOptions)
    );
  }, [monthOptions]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page tat-page sat-page"
      style={{ fontFamily: PAGE_FONT, background: "#ffffff" }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <Link to="/AdminMain/StaffManagement" style={{ color: COLORS.white }}>
                Staff Management
              </Link>
            </li>
            <li>
              <Link to="/AdminMain/StaffAttendanceDash" style={{ color: COLORS.white }}>
                Staff Attendance
              </Link>
            </li>
            <li>
              <span style={{ color: COLORS.white }}> Attendance Staff</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell sat-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaClipboardList />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Attendance Staff</h1>
              <p className="sm-hero__sub">
                Staff list · {selectedMonthLabel}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <Link to="/AdminMain/StaffMonthAttendance" className="tat-hero-link">
              <FaCalendarAlt aria-hidden />
              <span>Monthly View</span>
            </Link>
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={loadStaff}
              disabled={loading}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="sat-body">
          <section className="sat-month-panel" aria-label="Select month">
            <div className="sat-month-panel__head">
              <h2>
                <FaCalendarAlt aria-hidden /> Attendance month
              </h2>
              <span className="sat-month-panel__badge">
                {selectedMonthLabel}
              </span>
            </div>
            <div className="sat-month-track">
              <div
                className="sat-month-grid"
                role="group"
                aria-label="Session months"
                style={{
                  "--sat-month-count": Math.max(monthOptions.length, 1),
                }}
              >
                {monthOptions.map((item) => {
                  const active = item === selectedMonth;
                  const isCurrent = item === currentYearMonth;
                  return (
                    <button
                      key={item}
                      type="button"
                      className={`sat-month-chip${active ? " is-active" : ""}${
                        isCurrent && !active ? " is-current" : ""
                      }`}
                      onClick={() => setSelectedMonth(item)}
                      aria-pressed={active}
                      title={formatMonthLabel(item)}
                    >
                      <span className="sat-month-chip__name">
                        {formatMonthShort(item)}
                      </span>
                      <span className="sat-month-chip__year">
                        {isCurrent ? "Now" : monthYear(item)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <div className="sat-meta-card">
            <div className="sm-kpi-row sat-meta-chips" aria-label="Month summary">
              <KpiChip
                tone="blue"
                icon={FaCalendarAlt}
                label="Month"
                value={selectedMonthLabel}
              />
              <KpiChip
                tone="sky"
                icon={FaCalendarAlt}
                label="Month Day"
                value={monthStats.month_days}
              />
              <KpiChip
                tone="sand"
                icon={FaSun}
                label="Weekend"
                value={monthStats.weekends}
              />
              <KpiChip
                tone="sand"
                icon={FaGift}
                label="Holidays"
                value={monthStats.holidays}
              />
              <KpiChip
                tone="mint"
                icon={FaClipboardList}
                label="School Day"
                value={monthStats.school_days}
              />
              <KpiChip
                tone="mint"
                icon={FaCheckCircle}
                label="Average"
                value={`${totals.avgPct}% present`}
              />
            </div>
          </div>

          <div className="sm-kpi-row sm-kpi-row--six sat-kpis" aria-label="Month totals">
            <KpiChip tone="sky" icon={FaUsers} label="Staff" value={totals.count} />
            <KpiChip tone="mint" icon={FaCheckCircle} label="Present" value={totals.present} />
            <KpiChip tone="sand" icon={FaTimesCircle} label="Absent" value={totals.absent} />
            <KpiChip tone="blue" icon={FaUserClock} label="Leave" value={totals.leave} />
            <KpiChip tone="blue" icon={FaClipboardCheck} label="Half Day" value={totals.halfDay} />
            <KpiChip tone="sand" icon={FaUserClock} label="Late" value={totals.late} />
          </div>

          {loading && !staffRows.length ? (
            <div className="sat-loading">
              <MyLoader />
            </div>
          ) : staffRows.length === 0 ? (
            <div className="sat-empty-card">
              <span className="sat-empty-card__icon" aria-hidden>
                <FaClipboardList />
              </span>
              <h2>No staff found</h2>
              <p>No staff attendance records for {selectedMonthLabel}.</p>
            </div>
          ) : (
            <section className="sat-list-panel">
              <div className="sat-list-panel__head">
                <div className="min-w-0">
                  <h2 className="sat-list-panel__title">Staff list</h2>
                  <p className="sat-list-panel__hint">
                    {selectedMonthLabel} · tap a staff member for day-wise details
                  </p>
                </div>
                <div className="sat-list-panel__tools">
                  <label className="sat-search">
                    <FaSearch aria-hidden />
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search name, ID…"
                      aria-label="Search staff"
                    />
                  </label>
                  <span className="sat-list-panel__count">
                    {filteredStaff.length}
                    {search.trim() ? ` / ${staffRows.length}` : ""}
                  </span>
                </div>
              </div>

              <div className="sat-list-cols" aria-hidden>
                <span>Staff</span>
                <span>Month stats</span>
              </div>

              {filteredStaff.length === 0 ? (
                <div className="sat-empty sat-empty--soft">
                  No staff match “{search.trim()}”.
                </div>
              ) : (
                <div className="sat-student-list" role="list">
                  {filteredStaff.map((staff, index) => {
                    const name = staff.staff_name || "—";
                    const present = Number(staff.P || 0);
                    const absent = Number(staff.A || 0);
                    const leave = Number(staff.L || 0);
                    const halfDay = Number(staff.HD || 0);
                    const late = Number(staff.LA || 0);
                    const taken = Number(staff.taken_days || 0);
                    const pct =
                      taken === 0 ? 0 : Math.floor((present * 100) / taken);
                    return (
                      <Link
                        key={staff.staff_id || `${name}-${index}`}
                        role="listitem"
                        className="sat-student-row"
                        to={detailPath(staff, selectedMonth)}
                      >
                        <span className="sat-student-row__roll">
                          {index + 1}
                        </span>
                        <span className="sat-student-row__avatar">
                          {staff.staff_image_path ? (
                            <img
                              src={staff.staff_image_path}
                              alt=""
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const fallback =
                                  e.currentTarget.nextElementSibling;
                                if (fallback)
                                  fallback.style.display = "inline-flex";
                              }}
                            />
                          ) : null}
                          <span
                            className="sat-student-row__initials"
                            style={
                              staff.staff_image_path
                                ? { display: "none" }
                                : undefined
                            }
                            aria-hidden
                          >
                            {staffInitials(name) || "?"}
                          </span>
                        </span>
                        <span className="sat-student-row__body">
                          <strong>{name}</strong>
                          <small>
                            {staff.staff_id || "—"}
                            {staff.staff_role ? ` · ${staff.staff_role}` : ""}
                          </small>
                          <span className="sat-student-row__bar" aria-hidden>
                            <i style={{ width: `${Math.min(100, pct)}%` }} />
                          </span>
                        </span>
                        <span
                          className="sat-student-row__stats"
                          aria-label="Month stats"
                        >
                          <span className="is-p" title="Present">
                            <em>P</em>
                            <b>{present}</b>
                          </span>
                          <span className="is-a" title="Absent">
                            <em>A</em>
                            <b>{absent}</b>
                          </span>
                          <span className="is-l" title="Leave">
                            <em>L</em>
                            <b>{leave}</b>
                          </span>
                          <span className="is-l" title="Half Day">
                            <em>HD</em>
                            <b>{halfDay}</b>
                          </span>
                          <span className="is-a" title="Late">
                            <em>LA</em>
                            <b>{late}</b>
                          </span>
                          <span className="is-pct" title="Present %">
                            {pct}%
                          </span>
                        </span>
                        <span className="sat-student-row__go" aria-hidden>
                          <FaChevronRight />
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
